/**
 * @file CartProvider.tsx
 * @description Client-side cart core for the Learning Materials catalog: the React
 * context/provider, the consumer hook, shared label types/defaults, and the per-item
 * limit. Co-located here so the deeply-nested material cards and the sticky CartBar
 * stay in sync without prop drilling. (The component scanner registers the provider;
 * the remaining exports are plain helpers consumed within this folder.)
 */

'use client';

import { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode, RefObject } from 'react';

/** Fallback per-item maximum used when the CMS leaves cart_settings.max_allowed_quantity empty. */
export const MAX_ITEM_QUANTITY = 25;

/** Default copy shown when a requested quantity exceeds the allowed per-item maximum. */
export const DEFAULT_MAX_QUANTITY_ERROR = 'The requested quantity exceeds the maximum allowed.';

/** Default copy shown when the user tries to add an item without entering a quantity. */
export const DEFAULT_ZERO_QUANTITY_ERROR = 'Please specify a quantity.';

/** A single line item in the order bag. */
export interface CartItem {
  id: string;
  title: string;
  quantity: number;
}

/** Value exposed by the cart context. */
interface CartContextValue {
  items: CartItem[];
  totalItems: number;
  isOpen: boolean;
  maxAllowedQuantity: number;
  maxQuantityErrorMessage: string;
  zeroQuantityErrorMessage: string;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity: number) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  openCart: () => void;
  closeCart: () => void;
  // Element focused right before the cart opened (e.g. the "View order" trigger),
  // captured synchronously on click so OrderSummary can restore focus to it on close
  previousFocusRef: RefObject<HTMLElement | null>;
}

const CartContext = createContext<CartContextValue | null>(null);

/** Props that let the CMS cart_settings drive the per-item limit and its error copy. */
interface CartProviderProps {
  children: ReactNode;
  maxAllowedQuantity?: number | null;
  maxQuantityErrorMessage?: string;
  zeroQuantityErrorMessage?: string;
}

// Clamp any requested quantity into the allowed 1..max range
const clampQuantity = (quantity: number, max: number) => Math.max(1, Math.min(max, quantity));

export const injectNumberOfItemsWithinLabel = (label: string, totalItems: number) =>
  label.replace('${numberOfItems}', String(totalItems));
// Provider that owns the cart state for one Learning Materials region
export const CartProvider = ({
  children,
  maxAllowedQuantity,
  maxQuantityErrorMessage,
  zeroQuantityErrorMessage,
}: CartProviderProps) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Resolve the effective per-item ceiling, falling back to the default when the CMS leaves it empty
  const resolvedMaxQuantity =
    maxAllowedQuantity && maxAllowedQuantity > 0 ? maxAllowedQuantity : MAX_ITEM_QUANTITY;
  const resolvedErrorMessage = maxQuantityErrorMessage || DEFAULT_MAX_QUANTITY_ERROR;
  const resolvedZeroErrorMessage = zeroQuantityErrorMessage || DEFAULT_ZERO_QUANTITY_ERROR;

  // Set the quantity for a line item — re-adding an existing item replaces (not sums) its count
  const addItem = useCallback(
    (item: Omit<CartItem, 'quantity'>, quantity: number) => {
      if (quantity < 1) return;
      setItems((prev) => {
        const existing = prev.find((line) => line.id === item.id);
        if (existing) {
          return prev.map((line) =>
            line.id === item.id
              ? { ...line, quantity: clampQuantity(quantity, resolvedMaxQuantity) }
              : line
          );
        }
        return [...prev, { ...item, quantity: clampQuantity(quantity, resolvedMaxQuantity) }];
      });
    },
    [resolvedMaxQuantity]
  );

  // Set an exact quantity for a line item from the order summary editor
  const updateQuantity = useCallback(
    (id: string, quantity: number) => {
      setItems((prev) =>
        prev.map((line) =>
          line.id === id ? { ...line, quantity: clampQuantity(quantity, resolvedMaxQuantity) } : line
        )
      );
    },
    [resolvedMaxQuantity]
  );

  // Drop a line item entirely from the bag
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((line) => line.id !== id));
  }, []);

  // Capture the currently focused element before the trigger unmounts, so closing can restore it
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const openCart = useCallback(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    setIsOpen(true);
  }, []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  // Count of distinct line items in the bag (each material counts once, regardless of quantity)
  const totalItems = items.length;

  // Memoise the context value so consumers only re-render on real changes
  const value = useMemo<CartContextValue>(
    () => ({
      items,
      totalItems,
      isOpen,
      maxAllowedQuantity: resolvedMaxQuantity,
      maxQuantityErrorMessage: resolvedErrorMessage,
      zeroQuantityErrorMessage: resolvedZeroErrorMessage,
      addItem,
      updateQuantity,
      removeItem,
      openCart,
      closeCart,
      previousFocusRef,
    }),
    [
      items,
      totalItems,
      isOpen,
      resolvedMaxQuantity,
      resolvedErrorMessage,
      resolvedZeroErrorMessage,
      addItem,
      updateQuantity,
      removeItem,
      openCart,
      closeCart,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Hook to consume the cart; throws when used outside a CartProvider
export const useCart = (): CartContextValue => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
